import { useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { competitors as competitorsApi, watchedUrls as watchedUrlsApi, alerts as alertsApi } from "@/lib/api";
import type { Competitor, WatchedUrl, Alert } from "@/types";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import { Icon } from "@/components/ui/icon";
import { AlertSummary } from "@/components/alerts/AlertSummary";
import { timeAgo, pageTypeLabel } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { useWorkspaceRefresh } from "@/hooks/useWorkspaceRefresh";

const SIGNAL_NODE: Record<string, string> = {
  high: "bg-red-500 border-red-500",
  medium: "bg-amber-500 border-amber-500",
  low: "bg-zinc-400 border-zinc-400",
};
const SIGNAL_LINE: Record<string, string> = {
  high: "bg-red-500/20",
  medium: "bg-amber-500/20",
  low: "bg-zinc-400/20",
};

export function CompetitorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [competitor, setCompetitor] = useState<Competitor | null>(null);
  const [urls, setUrls] = useState<WatchedUrl[]>([]);
  const [recentAlerts, setRecentAlerts] = useState<Alert[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    if (!id) return;
    const [compRes, urlsRes, alertsRes] = await Promise.all([
      competitorsApi.list(),
      watchedUrlsApi.list(id),
      alertsApi.list({ competitorId: id, limit: 10 }),
    ]);
    if ("data" in compRes && compRes.data) {
      const found = compRes.data.find(c => c.id === id);
      if (found) setCompetitor(found);
    }
    if ("data" in urlsRes && urlsRes.data) setUrls(urlsRes.data);
    if ("data" in alertsRes && alertsRes.data) setRecentAlerts(alertsRes.data.alerts);
    setIsLoading(false);
  }, [id]);

  useWorkspaceRefresh(load);

  const handleDelete = async () => {
    if (!id || !confirm("Delete this competitor and all its data?")) return;
    const res = await competitorsApi.delete(id);
    if ("data" in res) {
      toast.success("Competitor deleted");
      navigate("/watchlist");
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex h-14 items-center border-b border-border px-6 gap-3">
          <Skeleton className="h-6 w-6 rounded" />
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="p-6 space-y-4 max-w-4xl">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  if (!competitor) {
    return (
      <div className="flex h-full flex-col">
        <PageHeader title="Competitor not found" />
        <div className="flex flex-col items-center gap-3 py-20 text-center">
          <Icon name="radar-01" size={28} className="text-muted-foreground/20" />
          <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Not found</p>
          <Button variant="outline" size="sm" onClick={() => navigate("/watchlist")}>Back to watchlist</Button>
        </div>
      </div>
    );
  }

  const faviconUrl = `https://www.google.com/s2/favicons?domain=${competitor.domain}&sz=32`;
  const successUrls = urls.filter(u => u.lastStatus === "success").length;
  const failedUrls = urls.filter(u => u.lastStatus === "failed" || u.lastStatus === "blocked").length;

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={competitor.name}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild className="text-[11px]">
              <a href={`https://${competitor.domain}`} target="_blank" rel="noopener noreferrer">
                <Icon name="link-01" size={11} className="mr-1.5" />
                Visit site
              </a>
            </Button>
            <Button variant="ghost" size="sm" onClick={handleDelete} className="text-[11px] text-destructive hover:text-destructive">
              <Icon name="delete-01" size={11} className="mr-1.5" />
              Delete
            </Button>
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto">
        <div className="flex gap-0 h-full">

          {/* Main */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">

            {/* Competitor header */}
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded border border-border bg-muted flex items-center justify-center shrink-0">
                <img src={faviconUrl} alt={competitor.name} className="h-7 w-7 object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                    (e.target as HTMLImageElement).parentElement!.innerHTML =
                      `<span class="font-mono text-xs font-bold uppercase">${competitor.name[0]}</span>`;
                  }} />
              </div>
              <div>
                <h2 className="font-sans text-lg font-semibold">{competitor.name}</h2>
                <a href={`https://${competitor.domain}`} target="_blank" rel="noopener noreferrer"
                  className="font-mono text-[11px] text-muted-foreground hover:text-foreground transition-colors">
                  {competitor.domain}
                </a>
              </div>
              <div className="ml-auto flex items-center gap-4">
                <div className="text-center">
                  <p className="font-mono text-2xl font-bold text-foreground">{urls.length}</p>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">URLs</p>
                </div>
                <div className="text-center">
                  <p className="font-mono text-2xl font-bold text-foreground">{recentAlerts.length}</p>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Alerts</p>
                </div>
                <div className="text-center">
                  <p className={cn("font-mono text-2xl font-bold", failedUrls > 0 ? "text-red-400" : "text-emerald-400")}>
                    {successUrls}
                  </p>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Active</p>
                </div>
              </div>
            </div>

            {/* Monitored URLs */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Monitored URLs</span>
                <Link to="/watchlist" className="font-mono text-[11px] text-muted-foreground hover:text-foreground transition-colors">
                  <span className="inline-flex items-center gap-1">Manage<Icon name="arrow-right-01" size={11} /></span>
                </Link>
              </div>
              {urls.length === 0 ? (
                <div className="rounded border border-dashed border-border p-6 text-center">
                  <p className="font-mono text-[11px] text-muted-foreground">No URLs monitored yet</p>
                </div>
              ) : (
                <div className="border border-border rounded divide-y divide-border">
                  {urls.map((wu) => (
                    <div key={wu.id} className="flex items-center gap-3 px-4 py-3">
                      <div className={cn("h-2 w-2 rounded-full shrink-0", {
                        "bg-emerald-500": wu.lastStatus === "success",
                        "bg-red-500": wu.lastStatus === "failed" || wu.lastStatus === "blocked",
                        "bg-amber-500 animate-pulse": wu.lastStatus === "running",
                        "border border-border bg-transparent": wu.lastStatus === "pending",
                      })} />
                      <div className="flex-1 min-w-0">
                        <p className="font-sans text-xs truncate">{wu.url}</p>
                      </div>
                      <span className="font-mono text-[11px] text-muted-foreground shrink-0">{wu.label}</span>
                      <span className="font-mono text-[11px] text-muted-foreground shrink-0">
                        {wu.lastCrawledAt ? timeAgo(wu.lastCrawledAt) : "pending"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent alerts — pipeline */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Recent Alerts</span>
                <Link to={`/alerts?competitorId=${id}`} className="font-mono text-[11px] text-muted-foreground hover:text-foreground transition-colors">
                  <span className="inline-flex items-center gap-1">View all<Icon name="arrow-right-01" size={11} /></span>
                </Link>
              </div>
              {recentAlerts.length === 0 ? (
                <div className="rounded border border-dashed border-border p-6 text-center">
                  <p className="font-mono text-[11px] text-muted-foreground">No alerts yet</p>
                </div>
              ) : (
                <div>
                  {recentAlerts.map((alert, i) => {
                    const isLast = i === recentAlerts.length - 1;
                    return (
                      <Link
                        key={alert.id}
                        to={`/alerts/${alert.id}`}
                        className="flex gap-4 group hover:bg-accent/20 transition-colors rounded -mx-2 px-2"
                      >
                        <div className="flex flex-col items-center shrink-0 w-5 pt-3.5">
                          <div className={cn(
                            "h-3 w-3 rounded-sm border-2 shrink-0 transition-all group-hover:scale-110",
                            SIGNAL_NODE[alert.signalScore] ?? "bg-zinc-400 border-zinc-400"
                          )} />
                          {!isLast && <div className={cn("w-px flex-1 mt-1 min-h-[20px]", SIGNAL_LINE[alert.signalScore] ?? "bg-border/50")} />}
                        </div>
                        <div className={cn("flex-1 min-w-0 py-3", !isLast && "pb-4")}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-[11px] text-muted-foreground">{pageTypeLabel(alert.pageType)}</span>
                            {alert.status === "unread" && <span className="h-1.5 w-1.5 rounded-full bg-foreground shrink-0" />}
                            <span className="ml-auto font-mono text-[11px] text-muted-foreground">{timeAgo(alert.detectedAt)}</span>
                          </div>
                          <AlertSummary summary={alert.summary} className="font-sans text-xs text-foreground/80 line-clamp-2 leading-relaxed" />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
